import { featureFlags } from '../utils/featureFlags';

export type PageMemoPageDefinition = {
  key: string;
  label: string;
  matches: (pathname: string) => boolean;
};

const isPath = (pathname: string, target: string) => pathname === target;
const startsWithPath = (pathname: string, target: string) =>
  pathname === target || pathname.startsWith(`${target}/`);

export const PAGE_MEMO_PAGES: PageMemoPageDefinition[] = [
  {
    key: '/app/dashboard',
    label: 'ダッシュボード',
    matches: (pathname) => isPath(pathname, '/app') || isPath(pathname, '/app/dashboard'),
  },
  { key: '/app/maintenance', label: '保全カレンダー', matches: (pathname) => startsWithPath(pathname, '/app/maintenance') },
  { key: '/app/my-tasks', label: 'マイタスク', matches: (pathname) => startsWithPath(pathname, '/app/my-tasks') },
  { key: '/app/work-order-templates', label: '作業指示書', matches: (pathname) => startsWithPath(pathname, '/app/work-order-templates') },
  { key: '/app/work-history', label: '作業履歴', matches: (pathname) => startsWithPath(pathname, '/app/work-history') },
  { key: '/app/emergency-works', label: '緊急作業', matches: (pathname) => startsWithPath(pathname, '/app/emergency-works') },
  { key: '/app/stock-dashboard', label: '在庫ダッシュボード', matches: (pathname) => startsWithPath(pathname, '/app/stock-dashboard') },
  { key: '/app/purchase-orders', label: '発注管理', matches: (pathname) => startsWithPath(pathname, '/app/purchase-orders') },
  { key: '/app/part-consumption-logs', label: '消費管理', matches: (pathname) => startsWithPath(pathname, '/app/part-consumption-logs') },
  { key: '/app/parts-analytics', label: '部品・消耗品分析', matches: (pathname) => startsWithPath(pathname, '/app/parts-analytics') },
  { key: '/app/deterioration', label: '健全度予測', matches: (pathname) => startsWithPath(pathname, '/app/deterioration') },
  { key: '/app/locations', label: 'エリア', matches: (pathname) => startsWithPath(pathname, '/app/locations') },
  { key: '/app/equipments', label: '設備', matches: (pathname) => startsWithPath(pathname, '/app/equipments') },
  { key: '/app/parts', label: '部品・消耗品', matches: (pathname) => startsWithPath(pathname, '/app/parts') },
  { key: '/app/suppliers', label: '業者', matches: (pathname) => startsWithPath(pathname, '/app/suppliers') },
  { key: '/app/documents', label: '書類', matches: (pathname) => startsWithPath(pathname, '/app/documents') },
  { key: '/app/notifications', label: '通知', matches: (pathname) => startsWithPath(pathname, '/app/notifications') },
  ...(featureFlags.qrLabelPrinting
    ? [
      {
        key: '/app/qr-labels',
        label: 'QRコードシール印刷',
        matches: (pathname) => startsWithPath(pathname, '/app/qr-labels'),
      },
    ]
    : []),
  { key: '/app/profile', label: 'プロフィール', matches: (pathname) => startsWithPath(pathname, '/app/profile') },
  { key: '/app/settings/dashboard', label: '会社設定ダッシュボード', matches: (pathname) => isPath(pathname, '/app/settings') || isPath(pathname, '/app/settings/dashboard') },
  { key: '/app/settings/company', label: '会社情報', matches: (pathname) => startsWithPath(pathname, '/app/settings/company') },
  { key: '/app/settings/branches', label: 'マップ', matches: (pathname) => startsWithPath(pathname, '/app/settings/branches') },
  { key: '/app/settings/advanced', label: '詳細設定', matches: (pathname) => startsWithPath(pathname, '/app/settings/advanced') },
  { key: '/app/settings/system-config', label: 'システム設定', matches: (pathname) => startsWithPath(pathname, '/app/settings/system-config') },
  { key: '/app/memos', label: '一元メモ設定', matches: (pathname) => startsWithPath(pathname, '/app/memos') },
  { key: '/app/settings/users', label: 'ユーザー管理', matches: (pathname) => startsWithPath(pathname, '/app/settings/users') },
  { key: '/app/settings/storage', label: 'ストレージ管理', matches: (pathname) => startsWithPath(pathname, '/app/settings/storage') },
  { key: '/app/settings/import', label: 'データインポート', matches: (pathname) => startsWithPath(pathname, '/app/settings/import') },
  { key: '/app/settings/export', label: 'データエクスポート', matches: (pathname) => startsWithPath(pathname, '/app/settings/export') },
  { key: '/app/settings/subscription', label: '契約プラン', matches: (pathname) => startsWithPath(pathname, '/app/settings/subscription') },
  { key: '/app/settings/withdraw', label: '退会', matches: (pathname) => startsWithPath(pathname, '/app/settings/withdraw') },
];

const PAGE_MEMO_LABELS = new Map(PAGE_MEMO_PAGES.map((page) => [page.key, page.label]));

const normalizePath = (pathname: string): string => {
  const withoutHash = pathname.split('#')[0] ?? pathname;
  return (withoutHash.split('?')[0] ?? withoutHash) || pathname;
};

export const resolvePageMemoInfo = (pathname: string): { pageKey: string; label: string } | null => {
  const normalized = normalizePath(pathname);
  if (!normalized.startsWith('/app')) {
    return null;
  }

  for (const page of PAGE_MEMO_PAGES) {
    if (page.matches(normalized)) {
      return { pageKey: page.key, label: page.label };
    }
  }

  const segments = normalized.split('/').filter(Boolean);
  if (segments.length >= 2) {
    const fallbackKey = `/app/${segments[1]}`;
    return {
      pageKey: fallbackKey,
      label: PAGE_MEMO_LABELS.get(fallbackKey) ?? 'ページメモ',
    };
  }

  return { pageKey: '/app/dashboard', label: 'ダッシュボード' };
};

export const resolvePageMemoKey = (pathname: string): string | null =>
  resolvePageMemoInfo(pathname)?.pageKey ?? null;

export const resolvePageMemoLabel = (pageKey: string): string =>
  PAGE_MEMO_LABELS.get(pageKey) ?? 'ページメモ';
